import React, { useCallback, useState } from "react"

import validator from "validator"

import OutsideClickHandler from "react-outside-click-handler"
import { LinkNode } from "@lexical/link"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material"
import { Check } from "@mui/icons-material"

import styles from "./FloatingLinkEditor.module.scss"
import MuiThemeProvider from "@web/components/Forms/MuiThemeProvider"
import { $createTextNode } from "lexical"
import cs from "classnames"

interface Props {
  onDone: () => void

  initialText?: string
  initialUrl?: string
  node: LinkNode
  element: HTMLElement
  className?: string
}

export function FloatingLinkEditor({ onDone, initialText = "", initialUrl = "", node, element, className }: Props) {
  const [editor] = useLexicalComposerContext()
  const [linkText, setLinkText] = useState(initialText)
  const [linkUrl, setLinkUrl] = useState(stripProtocol(initialUrl, ["mailto:"]))
  const [_isUnsubscribeLink, setIsUnsubscribeLink] = useState(element.getAttribute("data-unsubscribe-link") === "true")

  const handleTextInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setLinkText(e.target.value), [])

  const handleUrlInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setLinkUrl(e.target.value), [])

  const handleSubmit = useCallback(
    (e: { preventDefault: () => void }) => {
      e.preventDefault()

      editor.update(
        () => {
          const selection = node.select()
          let trimmedText = linkText.trim()
          let trimmedUrl = linkUrl.trim()

          // If there is no link text and no link URL - delete node and close
          if (!trimmedText && !trimmedUrl) {
            editor.update(() => node.remove())
            return
          }

          // If there is some link text and no link URL - use link text as link URL if it's a valid URL
          if (trimmedText && !trimmedUrl) {
            if (validator.isURL(trimmedText) || validator.isEmail(stripProtocol(trimmedText))) {
              trimmedUrl = ensureProtocol(trimmedText)
            } else {
              node.remove()
              selection?.insertNodes([$createTextNode(trimmedText)])
              return
            }
          }

          // If there is no link text and some link URL - treat link URL as link text
          if (!trimmedText && trimmedUrl) {
            trimmedText = stripProtocol(trimmedUrl)
          }

          const linkTextNodes = node.getAllTextNodes()
          const workTextNode = linkTextNodes[0]

          // Only keep a single text node inside the <a/> tag, set user-provided text inside of it
          linkTextNodes.forEach((textNode, i) => {
            if (i > 0) textNode.remove()
          })

          workTextNode?.setTextContent(stripProtocol(trimmedText))
          workTextNode?.setFormat(0)
          workTextNode?.setStyle("")

          // Update URL inside <a/>
          node.setURL(ensureProtocol(trimmedUrl))

          if (_isUnsubscribeLink) {
            element.setAttribute("data-unsubscribe-link", "true")
          } else {
            element.removeAttribute("data-unsubscribe-link")
          }

          // Move cursor to the end of the link
          if (workTextNode) {
            selection.setTextNodeRange(
              workTextNode,
              workTextNode.getTextContentSize(),
              workTextNode,
              workTextNode.getTextContentSize()
            )
          }
        },
        {
          onUpdate: onDone,
        }
      )
    },
    [editor, onDone, node, linkText, linkUrl, _isUnsubscribeLink, element]
  )

  /**
   * If link editor is closed via ESC or outside click and it was a fresh link - delete node and close
   */
  const discardChanges = useCallback(() => {
    if (!initialText.trim() && !initialUrl.trim()) {
      editor.update(() => node.remove())
    }

    onDone()
  }, [onDone, initialText, initialUrl, editor, node])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSubmit(e)
      } else if (e.key === "Escape") {
        e.preventDefault()
        discardChanges()
      }
    },
    [handleSubmit, discardChanges]
  )

  const handleUnsubscribeLinkToggle = () => {
    setIsUnsubscribeLink((currentIsUnsubscribeLink) => {
      if (currentIsUnsubscribeLink) {
        setLinkUrl("")
      } else {
        setLinkText((currentLinkText) => {
          if (!currentLinkText) return DEFAULT_UNSUBSCRIBE_LINK_TEXT

          return currentLinkText
        })
        setLinkUrl(CUSTOM_UNSUBSCRIBE_LINK_PLACEHOLDER)
      }
      return !currentIsUnsubscribeLink
    })
  }

  return (
    <MuiThemeProvider>
      <OutsideClickHandler onOutsideClick={discardChanges}>
        <div className={cs(styles.root, className)}>
          <div className={styles.inner}>
            <div className={styles.form}>
              <TextField
                label="Link text"
                value={linkText}
                onChange={handleTextInputChange}
                onKeyDown={handleInputKeyDown}
                autoFocus={!initialText}
              />
              <TextField
                label="Link URL"
                value={linkUrl}
                onChange={handleUrlInputChange}
                onKeyDown={handleInputKeyDown}
                autoFocus={!!initialText}
                disabled={_isUnsubscribeLink}
              />
              <div className="tmb-flex tmb-justify-between">
                <FormControlLabel
                  control={
                    <Checkbox
                      checkedIcon={<Check />}
                      onChange={handleUnsubscribeLinkToggle}
                      checked={_isUnsubscribeLink}
                    />
                  }
                  label="Unsubscribe link"
                />
                <Button onClick={handleSubmit} size="medium" type="button">
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </OutsideClickHandler>
    </MuiThemeProvider>
  )
}

export function ensureProtocol(input: string): string {
  const trimmedInput = input.trim()

  if (/^mailto:/i.test(trimmedInput)) {
    // It's an email address with mailto:
    return trimmedInput
  } else if (/^http(s)?:\/\//i.test(trimmedInput)) {
    // It's a URL with http:// or https://
    return trimmedInput
  } else if (validator.isEmail(trimmedInput)) {
    // It's an email address without mailto:
    return "mailto:" + trimmedInput
  } else if (validator.isURL(trimmedInput)) {
    // It's a URL without http:// or https://
    return "http://" + trimmedInput
  }

  return trimmedInput
}

function stripProtocol(input: string, protocols = ["mailto:" as const, "http(s)?://" as const]): string {
  if (!protocols.length) return input

  return input.replace(new RegExp(`^(${protocols.map((p) => `(${p})`).join("|")})`, "i"), "")
}

const DEFAULT_UNSUBSCRIBE_LINK_TEXT = "Click here to unsubscribe"
const CUSTOM_UNSUBSCRIBE_LINK_PLACEHOLDER = "https://tombo.ai/unsubscribe"
